// Building blocks shared by the wave-5 panels (decision D23): durations and minutes, cost with the calls whose cost
// is unknown (never shown as zero), and the per-model / per-agent usage tables of the production accounting.
import type { ReactNode } from "react";
import { useI18n, type Translate } from "../../i18n/index.tsx";
import type { MessageKey } from "../../i18n/en.ts";
import { formatCompactDuration } from "../../lib/duration.ts";
import { formatCents, formatNumber } from "../../lib/format.ts";
import { formatRatio } from "../agents/shared.tsx";
import type { AgentUsage, ModelUsage } from "../../api/types-wave5.ts";

/** Translated label of a server value, or the raw value when the catalog has no entry for it. */
export function labelOr(t: Translate, key: string, raw: string): string {
  const label = t(key as MessageKey);
  return label === key ? raw : label;
}

/** Compact duration ("3h 05m"); "—" when not measured. */
export function formatDuration(ms: number | null | undefined): string {
  return ms === null || ms === undefined ? "—" : (formatCompactDuration(ms) ?? "—");
}

/** Minutes measured by the API ("42m", "3h 10m"); "—" when nothing was measured. */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return "—";
  return minutes < 60 ? `${Math.round(minutes)}m` : formatDuration(Math.round(minutes) * 60_000);
}

/** Known cost plus, when some calls had no price, how many of them (the total is then a lower bound). */
export function CostText({ cents, unknownCalls }: { cents: number; unknownCalls: number }) {
  const { t, locale } = useI18n();
  return (
    <span className="cost-text" data-testid="production-cost">
      {formatCents(cents, locale)}
      {unknownCalls > 0 ? (
        <span className="badge badge--warning cost-unknown" data-testid="production-unknown-cost">
          {t("production.unknownCost", { n: unknownCalls })}
        </span>
      ) : null}
    </span>
  );
}

export function Stat({ label, value, testId }: { label: string; value: ReactNode; testId?: string }) {
  return (
    <div className="stat" data-testid={testId}>
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

export function ModelsTable({ models, label }: { models: ModelUsage[]; label: string }) {
  const { t, locale } = useI18n();
  if (models.length === 0) return <p className="subtle">{t("production.noModels")}</p>;
  return (
    <div className="table-wrap" role="region" aria-label={label} tabIndex={0}>
      <table className="table" data-testid="production-models">
        <thead>
          <tr>
            <th scope="col">{t("production.col.model")}</th>
            <th scope="col" className="num">{t("production.col.calls")}</th>
            <th scope="col" className="num">{t("production.col.errorRate")}</th>
            <th scope="col" className="num">{t("production.col.tokens")}</th>
            <th scope="col" className="num">{t("production.col.cost")}</th>
          </tr>
        </thead>
        <tbody>
          {models.map((m) => (
            <tr key={m.model}>
              <th scope="row" className="mono">
                {m.model}
              </th>
              <td className="num">{formatNumber(m.calls, locale)}</td>
              <td className="num">{formatRatio(m.error_rate, locale)}</td>
              <td className="num">{t("production.tokensValue", { input: formatNumber(m.tokens.input, locale), output: formatNumber(m.tokens.output, locale) })}</td>
              <td className="num">
                <CostText cents={m.known_cost_cents} unknownCalls={m.unknown_cost_calls} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type AgentRow = AgentUsage & { share_of_done_pct?: number | null; share_of_work_pct?: number | null };

/** Agents with attempts and success rate; with the project shares when given (% of done tasks and of work time). */
export function AgentsTable({ agents, label, shares = false }: { agents: AgentRow[]; label: string; shares?: boolean }) {
  const { t, locale } = useI18n();
  const pct = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${formatNumber(v, locale)}%`);
  if (agents.length === 0) return <p className="subtle">{t("production.noAgents")}</p>;
  return (
    <div className="table-wrap" role="region" aria-label={label} tabIndex={0}>
      <table className="table" data-testid="production-agents">
        <thead>
          <tr>
            <th scope="col">{t("production.col.agent")}</th>
            <th scope="col" className="num">{t("production.col.attempts")}</th>
            <th scope="col" className="num">{t("production.col.successRate")}</th>
            {shares ? <th scope="col" className="num">{t("production.col.shareDone")}</th> : null}
            {shares ? <th scope="col" className="num">{t("production.col.shareWork")}</th> : null}
          </tr>
        </thead>
        <tbody>
          {agents.map((a) => (
            <tr key={`${a.agent_profile_id ?? "default"}:${a.role}`}>
              <th scope="row">
                <span className="user-content">{a.agent}</span> <span className="subtle">· {labelOr(t, `agentRole.${a.role}`, a.role)}</span>
              </th>
              <td className="num">{formatNumber(a.attempts, locale)}</td>
              <td className="num">{formatRatio(a.success_rate, locale)}</td>
              {shares ? <td className="num">{pct(a.share_of_done_pct)}</td> : null}
              {shares ? <td className="num">{pct(a.share_of_work_pct)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
